import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  FileText, 
  User, 
  Target, 
  Briefcase,
  Star,
  Eye,
  Edit3,
  Save,
  Check,
  Clock,
  Download
} from 'lucide-react';
import { LinkedInWizardData } from '@/pages/LinkedInWizard';

interface LinkedInWizardContentReviewProps {
  data: LinkedInWizardData;
  onNext: () => void;
  onBack: () => void;
  onSaveRawContent: (contentData: any) => void;
}

export default function LinkedInWizardContentReview({ 
  data, 
  onNext, 
  onBack, 
  onSaveRawContent 
}: LinkedInWizardContentReviewProps) {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editedData, setEditedData] = useState(data);
  const [isSaving, setIsSaving] = useState(false);

  const handleEditSection = (section: string) => {
    setEditingSection(section);
  };

  const handleSaveSection = () => {
    setEditingSection(null);
  };

  const handleSaveRawContent = async () => {
    setIsSaving(true);
    
    const rawContentData = {
      formResponses: {
        userCategories: editedData.userCategories,
        primaryGoals: editedData.primaryGoals,
        uniqueSellingPoints: editedData.uniqueSellingPoints,
        customUSP: editedData.customUSP,
        targetAudience: editedData.targetAudience,
        whatYouHelp: editedData.whatYouHelp,
        howYouHelp: editedData.howYouHelp,
        valueProposition: editedData.valueProposition,
        industries: editedData.industries,
        audienceSeniority: editedData.audienceSeniority,
        companySize: editedData.companySize,
        voiceStyle: editedData.voiceStyle,
        includeFaith: editedData.includeFaith,
        keywords: editedData.keywords,
        targetJobTitles: editedData.targetJobTitles,
        pastJobTitles: editedData.pastJobTitles,
        skills: editedData.skills,
        callToAction: editedData.callToAction
      },
      textContent: {
        pastedText: editedData.pastedText,
        manuallyEnteredText: editedData.manuallyEnteredText,
        resultsAndProof: editedData.resultsAndProof
      },
      uploadedFiles: editedData.uploadedFiles,
      experienceHighlights: editedData.experienceHighlights,
      timestamp: new Date().toISOString(),
      profileName: `LinkedIn Profile - ${new Date().toLocaleDateString()}`,
      useCase: 'LinkedIn Profile Optimization',
      description: 'Complete LinkedIn profile optimization with collected content and form responses'
    };

    await onSaveRawContent(rawContentData);
    setIsSaving(false);
  };

  const renderFormSection = (title: string, content: any, sectionKey: string) => {
    const isEditing = editingSection === sectionKey;
    
    return (
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{title}</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => isEditing ? handleSaveSection() : handleEditSection(sectionKey)}
            >
              {isEditing ? <Save className="w-4 h-4 mr-1" /> : <Edit3 className="w-4 h-4 mr-1" />}
              {isEditing ? 'Save' : 'Edit'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-3">
              {Array.isArray(content) ? (
                <div className="space-y-2">
                  {content.map((item, index) => (
                    <Badge key={index} variant="secondary" className="mr-2 mb-2">
                      {item}
                    </Badge>
                  ))}
                </div>
              ) : (
                <Textarea
                  value={content || ''}
                  onChange={(e) => {
                    setEditedData(prev => ({ ...prev, [sectionKey]: e.target.value }));
                  }}
                  className="min-h-[100px]"
                />
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {Array.isArray(content) ? (
                content.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {content.map((item, index) => (
                      <Badge key={index} variant="outline">
                        {item}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground italic">No items selected</span>
                )
              ) : (
                content ? (
                  <p className="whitespace-pre-wrap">{content}</p>
                ) : (
                  <span className="text-muted-foreground italic">No content provided</span>
                )
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderFileSection = () => {
    return (
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Uploaded Files
          </CardTitle>
          <CardDescription>
            {editedData.uploadedFiles?.length || 0} files uploaded
          </CardDescription>
        </CardHeader>
        <CardContent>
          {editedData.uploadedFiles && editedData.uploadedFiles.length > 0 ? (
            <div className="space-y-3">
              {editedData.uploadedFiles.map((file: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{file.fileName || `File ${index + 1}`}</p>
                      <p className="text-sm text-muted-foreground">
                        {file.fileType || 'Unknown'} • {(file.fileSize / 1024).toFixed(1)}KB
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">
                    {file.extractedText ? `${file.extractedText.length} chars` : 'Processing...'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No files uploaded</p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Content Review & Save</h1>
            <p className="text-muted-foreground">
              Review all collected content before generating your LinkedIn profile
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <User className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Form Responses</p>
              <p className="text-xs text-muted-foreground">Complete profile data</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 mx-auto mb-2 text-success" />
              <p className="text-sm font-medium">Files Uploaded</p>
              <p className="text-xs text-muted-foreground">{editedData.uploadedFiles?.length || 0} documents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-warning" />
              <p className="text-sm font-medium">Ready to Save</p>
              <p className="text-xs text-muted-foreground">All content collected</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="profile-data" className="mb-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile-data">Profile Data</TabsTrigger>
          <TabsTrigger value="text-content">Text Content</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="profile-data">
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-4">
              {renderFormSection("User Categories", editedData.userCategories, "userCategories")}
              {renderFormSection("Primary Goals", editedData.primaryGoals, "primaryGoals")}
              {renderFormSection("Target Audience", editedData.targetAudience, "targetAudience")}
              {renderFormSection("Value Proposition", editedData.valueProposition, "valueProposition")}
              {renderFormSection("Industries", editedData.industries, "industries")}
              {renderFormSection("Keywords", editedData.keywords, "keywords")}
              {renderFormSection("Target Job Titles", editedData.targetJobTitles, "targetJobTitles")}
              {renderFormSection("Skills", editedData.skills, "skills")}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="text-content">
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-4">
              {renderFormSection("Pasted Text", editedData.pastedText, "pastedText")}
              {renderFormSection("Manually Entered Text", editedData.manuallyEnteredText, "manuallyEnteredText")}
              {renderFormSection("Results and Proof", editedData.resultsAndProof, "resultsAndProof")}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="files">
          <ScrollArea className="h-[600px] pr-4">
            {renderFileSection()}
          </ScrollArea>
        </TabsContent>

        <TabsContent value="summary">
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Content Summary</CardTitle>
                  <CardDescription>Overview of all collected content</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Categories Selected</Label>
                      <p className="text-sm text-muted-foreground">
                        {editedData.userCategories?.length || 0} categories
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Goals Defined</Label>
                      <p className="text-sm text-muted-foreground">
                        {editedData.primaryGoals?.length || 0} goals
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Files Uploaded</Label>
                      <p className="text-sm text-muted-foreground">
                        {editedData.uploadedFiles?.length || 0} documents
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Text Content</Label>
                      <p className="text-sm text-muted-foreground">
                        {[editedData.pastedText, editedData.manuallyEnteredText, editedData.resultsAndProof]
                          .filter(Boolean).length} sections
                      </p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Content Completeness</Label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, (
                              (editedData.userCategories?.length || 0) +
                              (editedData.primaryGoals?.length || 0) +
                              (editedData.uploadedFiles?.length || 0) +
                              ([editedData.pastedText, editedData.manuallyEnteredText].filter(Boolean).length)
                            ) / 20 * 100)}%`
                          }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {Math.min(100, Math.round((
                          (editedData.userCategories?.length || 0) +
                          (editedData.primaryGoals?.length || 0) +
                          (editedData.uploadedFiles?.length || 0) +
                          ([editedData.pastedText, editedData.manuallyEnteredText].filter(Boolean).length)
                        ) / 20 * 100))}% Complete
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          Back to Review
        </Button>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleSaveRawContent}
            disabled={isSaving}
            className="flex items-center gap-2"
          >
            {isSaving ? (
              <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Saving...' : 'Save Raw Content'}
          </Button>
          
          <Button onClick={onNext} className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            Generate LinkedIn Profile
          </Button>
        </div>
      </div>
    </div>
  );
}